/**
 * wsrv.nl Image Optimization Utility
 * https://wsrv.nl/docs
 *
 * Converts any CDN image URL into a wsrv.nl optimized URL with:
 * - automatic format negotiation (webp/avif)
 * - width-based resizing
 * - quality control
 * - srcset generation
 */

const WSRV_BASE = "https://wsrv.nl";

/**
 * Check if URL should be optimized via wsrv
 */
export function shouldOptimize(src) {
  if (!src || typeof src !== "string") return false;
  if (src.startsWith("data:")) return false;
  if (src.startsWith("blob:")) return false;
  // Local static assets - no need for wsrv
  if (src.startsWith("/") && !src.startsWith("//")) return false;
  // Skip SVG vectors for wsrv (already optimized, small, vector)
  // But still proxy them to avoid third-party cookies from origin CDN
  // if (src.toLowerCase().split("?")[0].endsWith(".svg")) return false;
  // Only optimize http(s) URLs
  return src.startsWith("http://") || src.startsWith("https://");
}

/**
 * Build a wsrv.nl URL for a given source and params
 *
 * @param {string} src - original image URL
 * @param {object} opts
 * @param {number} opts.w - width
 * @param {number} opts.h - height (optional)
 * @param {number} opts.q - quality 1-100 (default 80)
 * @param {string} opts.output - webp | avif | jpg | png (default webp)
 * @param {string} opts.fit - cover | contain | inside | outside
 * @param {number} opts.dpr - device pixel ratio
 * @returns {string} wsrv optimized URL
 */
export function getWsrvUrl(src, { w, h, q = 80, output = "webp", fit, dpr } = {}) {
  if (!shouldOptimize(src)) return src;

  const params = new URLSearchParams();
  // wsrv.nl requires `url` param - it auto-encodes
  params.set("url", src);
  if (w) params.set("w", String(Math.round(w)));
  if (h) params.set("h", String(Math.round(h)));
  if (q) params.set("q", String(q));
  if (output) params.set("output", output);
  if (fit) params.set("fit", fit);
  if (dpr) params.set("dpr", String(dpr));
  // Enable sharp? default is fine
  // `n` = -1 disables cache buster? keep default
  // Add `af` = auto format? but we enforce output=webp for best compression
  // Use `ll` = 1? no

  return `${WSRV_BASE}/?${params.toString()}`;
}

/**
 * Generate srcSet string for responsive images
 *
 * @param {string} src - original URL
 * @param {number[]} widths - array of widths
 * @param {object} opts - extra opts passed to getWsrvUrl (q, output, h, fit)
 * @returns {string} srcSet attribute value
 */
export function getSrcSet(src, widths = [320, 480, 640, 750, 828, 1080, 1200, 1920], opts = {}) {
  if (!shouldOptimize(src)) return undefined;
  return widths.map((w) => `${getWsrvUrl(src, { ...opts, w })} ${w}w`).join(", ");
}

/**
 * Generate srcSet with DPR variants (1x, 2x) for fixed-size images
 * Useful for logos, thumbnails where display size is known
 */
export function getDprSrcSet(src, baseWidth, opts = {}) {
  if (!shouldOptimize(src)) return undefined;
  return [1, 2].map((dpr) => `${getWsrvUrl(src, { ...opts, w: baseWidth * dpr })} ${dpr}x`).join(", ");
}

/**
 * Helper for background images - returns wsrv URL at appropriate width
 */
export function getBgWsrvUrl(src, width = 1920, opts = {}) {
  return getWsrvUrl(src, { w: width, ...opts });
}

/**
 * Default responsive widths presets
 */
export const PRESETS = {
  // Full-screen hero / banner (100vw)
  hero: [640, 750, 828, 1080, 1200, 1920, 2048, 3840],
  // Card / grid images
  card: [320, 480, 640, 750, 828],
  // Thumbnail / book cover
  thumb: [160, 240, 320, 480, 640],
  // Logo
  logo: [96, 128, 192, 256],
};

export default getWsrvUrl;
